/**
 * Hook to fetch all token balances from a user's wallet.
 * Uses the Euler Indexer token list and fetches balances via multicall.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAccount, usePublicClient, useBalance } from 'wagmi'
import { type Address, erc20Abi, formatUnits } from 'viem'
import { useEulerIndexerTokens } from '../token/tokensQuery'
import { getPriceFromMap, usePricesFromIndexer } from '../oracle/usePricesFromIndexer'
import { E_EARN_VAULT_CHAIN_ID } from '@/features/vaults/logic/useEVaults'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import type { NormalizedTokenInfo } from '../token/types'
import { NATIVE_HYPE_ADDRESS, WHYPE_ADDRESS_HYPEREVM } from '../nativeHype'

export interface WalletTokenBalance {
  token: Token
  balance: NormalizedUnitNumber
  balanceUSD: NormalizedUnitNumber
  logoURI?: string
}

export interface UseWalletTokenBalancesResult {
  /** All tokens with non-zero balances */
  tokens: WalletTokenBalance[]
  /** All known tokens (including zero balance) */
  allTokens: WalletTokenBalance[]
  /** Loading state */
  isLoading: boolean
  /** Error if any */
  error: Error | null
  /** Refetch function */
  refetch: () => void
}

/**
 * Fetch balances for multiple tokens using multicall
 */
async function fetchTokenBalances(
  publicClient: ReturnType<typeof usePublicClient>,
  userAddress: Address,
  tokens: NormalizedTokenInfo[],
): Promise<Map<Address, bigint>> {
  if (!publicClient || tokens.length === 0) {
    return new Map()
  }

  const contracts = tokens.map((token) => ({
    address: token.address as Address,
    abi: erc20Abi,
    functionName: 'balanceOf' as const,
    args: [userAddress] as const,
  }))

  const results = await publicClient.multicall({
    contracts,
    allowFailure: true,
  })

  const balanceMap = new Map<Address, bigint>()

  for (let i = 0; i < results.length; i++) {
    const result = results[i]
    const token = tokens[i]

    if (result && token && result.status === 'success' && result.result !== undefined) {
      balanceMap.set(token.address, result.result as bigint)
    }
  }

  return balanceMap
}

/**
 * Hook to get all token balances for the connected wallet
 */
export function useWalletTokenBalances(chainId: number = E_EARN_VAULT_CHAIN_ID): UseWalletTokenBalancesResult {
  const { address: userAddress } = useAccount()
  const publicClient = usePublicClient({ chainId })

  // Get all known tokens from Euler Indexer
  const { data: tokenMap, isLoading: isTokensLoading, error: tokensError } = useEulerIndexerTokens(chainId)

  // Fetch USD prices from Euler Indexer (used to compute balanceUSD + Token.unitPriceUsd)
  const { data: pricesMap } = usePricesFromIndexer(chainId)

  // Fetch native HYPE balance
  const { data: nativeBalance } = useBalance({
    address: userAddress,
    chainId,
    query: {
      enabled: !!userAddress && chainId === E_EARN_VAULT_CHAIN_ID,
      refetchInterval: 60_000,
    },
  })

  // Convert token map to array
  const tokenList = useMemo(() => {
    if (!tokenMap) return []
    return Array.from(tokenMap.values())
  }, [tokenMap])

  // Fetch balances for all tokens
  const {
    data: balanceMap,
    isLoading: isBalancesLoading,
    error: balancesError,
    refetch,
  } = useQuery({
    queryKey: ['walletTokenBalances', chainId, userAddress, tokenList.length],
    queryFn: async () => {
      if (!publicClient || !userAddress || tokenList.length === 0) {
        return new Map<Address, bigint>()
      }
      return fetchTokenBalances(publicClient, userAddress, tokenList)
    },
    enabled: !!publicClient && !!userAddress && tokenList.length > 0,
    staleTime: 30_000, // 30 seconds
    gcTime: 5 * 60_000, // 5 minutes
    refetchInterval: 60_000, // Refetch every minute
  })

  // Transform to WalletTokenBalance format
  const allTokens = useMemo((): WalletTokenBalance[] => {
    if (!tokenList || !balanceMap) return []

    return tokenList.map((tokenInfo) => {
      const balanceRaw = balanceMap.get(tokenInfo.address) ?? 0n
      const balanceFormatted = formatUnits(balanceRaw, tokenInfo.decimals)
      const balance = NormalizedUnitNumber(balanceFormatted)

      const priceUSD = getPriceFromMap(pricesMap, tokenInfo.address)

      const token = new Token({
        symbol: TokenSymbol(tokenInfo.symbol),
        name: tokenInfo.name,
        decimals: tokenInfo.decimals,
        address: CheckedAddress(tokenInfo.address),
        unitPriceUsd: priceUSD.toString(),
        logoURI: tokenInfo.logoURI,
      })

      const balanceUSD = token.toUSD(balance)

      return {
        token,
        balance,
        balanceUSD,
        logoURI: tokenInfo.logoURI,
      }
    })
  }, [tokenList, balanceMap, pricesMap])

  // Add native HYPE and ensure WHYPE is present if either has balance
  // They should always be shown together as a pair
  const allTokensWithNativeHype = useMemo(() => {
    const tokens = [...allTokens]

    if (chainId === E_EARN_VAULT_CHAIN_ID) {
      const nativeHypeBalance = nativeBalance
        ? NormalizedUnitNumber(formatUnits(nativeBalance.value, nativeBalance.decimals))
        : NormalizedUnitNumber('0')
      const whypeToken = tokens.find((t) => t.token.address.toLowerCase() === WHYPE_ADDRESS_HYPEREVM.toLowerCase())
      const whypeBalance = whypeToken?.balance ?? NormalizedUnitNumber('0')
      const whypePriceUSD = getPriceFromMap(pricesMap, WHYPE_ADDRESS_HYPEREVM as Address)

      // If either HYPE or WHYPE has balance, ensure both are in the list
      const shouldShowBoth = nativeHypeBalance.gt(0) || whypeBalance.gt(0)

      if (shouldShowBoth) {
        // Add native HYPE if not already present
        const hasNativeHype = tokens.some((t) => t.token.address.toLowerCase() === NATIVE_HYPE_ADDRESS.toLowerCase())

        if (!hasNativeHype && nativeBalance) {
          const unitPriceUsd = (whypeToken?.token.unitPriceUsd.toFixed() ?? whypePriceUSD.toString()) || '0'
          const nativeHypeToken = new Token({
            symbol: TokenSymbol('HYPE'),
            name: 'HYPE',
            decimals: nativeBalance.decimals,
            address: CheckedAddress(NATIVE_HYPE_ADDRESS),
            unitPriceUsd,
            logoURI: whypeToken?.logoURI,
          })

          tokens.push({
            token: nativeHypeToken,
            balance: nativeHypeBalance,
            balanceUSD: nativeHypeToken.toUSD(nativeHypeBalance),
            logoURI: whypeToken?.logoURI,
          })
        }

        // Add WHYPE if not already present (e.g., user only has native HYPE)
        if (!whypeToken) {
          // Find WHYPE in the original token list to get its info
          const whypeTokenInfo = tokenList.find((t) => t.address.toLowerCase() === WHYPE_ADDRESS_HYPEREVM.toLowerCase())

          if (whypeTokenInfo) {
            const whypeTokenObj = new Token({
              symbol: TokenSymbol(whypeTokenInfo.symbol),
              name: whypeTokenInfo.name,
              decimals: whypeTokenInfo.decimals,
              address: CheckedAddress(whypeTokenInfo.address),
              unitPriceUsd: whypePriceUSD.toString(),
              logoURI: whypeTokenInfo.logoURI,
            })

            tokens.push({
              token: whypeTokenObj,
              balance: NormalizedUnitNumber('0'),
              balanceUSD: NormalizedUnitNumber('0'),
              logoURI: whypeTokenInfo.logoURI,
            })
          }
        }
      }
    }

    return tokens
  }, [allTokens, nativeBalance, chainId, tokenList, pricesMap])

  // Filter to only tokens with non-zero balance
  const tokensWithBalance = useMemo(() => {
    return allTokensWithNativeHype.filter((t) => t.balance.gt(0))
  }, [allTokensWithNativeHype])

  return {
    tokens: tokensWithBalance,
    allTokens: allTokensWithNativeHype,
    isLoading: isTokensLoading || isBalancesLoading,
    error: (tokensError || balancesError) as Error | null,
    refetch,
  }
}

/**
 * Hook to get a specific token's balance from the wallet
 */
export function useWalletTokenBalance(
  tokenAddress: Address | undefined,
  chainId: number = E_EARN_VAULT_CHAIN_ID,
): WalletTokenBalance | undefined {
  const { tokens } = useWalletTokenBalances(chainId)

  return useMemo(() => {
    if (!tokenAddress) return undefined
    return tokens.find((t) => t.token.address.toLowerCase() === tokenAddress.toLowerCase())
  }, [tokens, tokenAddress])
}
